/**
 * @license Copyright (c) 2014-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor.js';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat.js';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote.js';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold.js';
import CloudServices from '@ckeditor/ckeditor5-cloud-services/src/cloudservices.js';
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials.js';
import Heading from '@ckeditor/ckeditor5-heading/src/heading.js';
import Image from '@ckeditor/ckeditor5-image/src/image.js';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption.js';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle.js';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar.js';
import Indent from '@ckeditor/ckeditor5-indent/src/indent.js';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic.js';
import Link from '@ckeditor/ckeditor5-link/src/link.js';
import List from '@ckeditor/ckeditor5-list/src/list.js';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed.js';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph.js';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice.js';
import Table from '@ckeditor/ckeditor5-table/src/table.js';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar.js';
import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation.js';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize';
import AutoLink from '@ckeditor/ckeditor5-link/src/autolink';
import Font from '@ckeditor/ckeditor5-font/src/font';
import FindAndReplace from '@ckeditor/ckeditor5-find-and-replace/src/findandreplace';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import UploadAdapter from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter';
import { ImageInsert } from '@ckeditor/ckeditor5-image';
import HtmlEmbed from '@ckeditor/ckeditor5-html-embed/src/htmlembed';
import SourceEditing from '@ckeditor/ckeditor5-source-editing/src/sourceediting';

class Editor extends ClassicEditor {}

// Plugins to include in the build.
Editor.builtinPlugins = [
	Autoformat,
	UploadAdapter,
	BlockQuote,
	Bold,
	CloudServices,
	Essentials,
	Heading,
	Image,
	ImageCaption,
	ImageStyle,
	ImageToolbar,
	ImageInsert,
	Indent,
	Italic,
	Link,
	List,
	MediaEmbed,
	Paragraph,
	PasteFromOffice,
	Table,
	TableToolbar,
	TextTransformation,
	ImageResize,
	AutoLink,
	Font,
	FindAndReplace,
	Alignment,
	HtmlEmbed,
	SourceEditing
];

// Editor configuration.
Editor.defaultConfig = {
	toolbar: {
		items: [
			'sourceEditing',
			'|',
			'heading',
			'|',
			'fontSize',
			'fontFamily',
			'fontColor',
			'fontBackgroundColor',
			'|',
			'bold',
			'italic',
			'alignment',
			'|',
			'link',
			'findAndReplace',
			'|',
			'bulletedList',
			'numberedList',
			'|',
			'outdent',
			'indent',
			'|',
			'mediaEmbed',
			'insertImage',
			'htmlEmbed',
			'blockQuote',
			'insertTable',
			'undo',
			'redo'
		]
	},
	language: 'en',
	image: {
		toolbar: [
			'imageTextAlternative',
			'imageStyle:inline',
			'imageStyle:block',
			'imageStyle:side'
		]
	},
	table: {
		contentToolbar: [
			'tableColumn',
			'tableRow',
			'mergeTableCells'
		]
	},
	fontColor: {
		colors: [
			{
				color: 'hsl(0, 0%, 0%)',
				label: ''
			},
			{
				color: 'hsl(0, 0%, 12.5%)',
				label: ''
			},
			{
				color: 'hsl(0, 0%, 25%)',
				label: ''
			},
			{
				color: 'hsl(0, 0%, 37.5%)',
				label: ''
			},
			{
				color: 'hsl(0, 0%, 50%)',
				label: ''
			},
			{
				color: 'hsl(0, 0%, 62.5%)',
				label: ''
			},
			{
				color: 'hsl(0, 0%, 75%)',
				label: ''
			},
			{
				color: 'hsl(0, 0%, 87.5%)',
				label: ''
			},
			{
				color: 'hsl(0, 0%, 100%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(330, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(330, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(330, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(330, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(330, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(330, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(330, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(330, 100%, 90%)',
				label: ''
			},
		],
		columns: 9,
	},
	fontBackgroundColor: {
		colors: [
			{
				color: 'hsl(0, 0%, 0%)',
				label: ''
			},
			{
				color: 'hsl(0, 0%, 12.5%)',
				label: ''
			},
			{
				color: 'hsl(0, 0%, 25%)',
				label: ''
			},
			{
				color: 'hsl(0, 0%, 37.5%)',
				label: ''
			},
			{
				color: 'hsl(0, 0%, 50%)',
				label: ''
			},
			{
				color: 'hsl(0, 0%, 62.5%)',
				label: ''
			},
			{
				color: 'hsl(0, 0%, 75%)',
				label: ''
			},
			{
				color: 'hsl(0, 0%, 87.5%)',
				label: ''
			},
			{
				color: 'hsl(0, 0%, 100%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(0, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(30, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(60, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(90, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(120, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(150, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(180, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(210, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(240, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(270, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 90%)',
				label: ''
			},
			{
				color: 'hsl(330, 100%, 10%)',
				label: ''
			},
			{
				color: 'hsl(330, 100%, 20%)',
				label: ''
			},
			{
				color: 'hsl(300, 100%, 30%)',
				label: ''
			},
			{
				color: 'hsl(330, 100%, 40%)',
				label: ''
			},
			{
				color: 'hsl(330, 100%, 50%)',
				label: ''
			},
			{
				color: 'hsl(330, 100%, 60%)',
				label: ''
			},
			{
				color: 'hsl(330, 100%, 70%)',
				label: ''
			},
			{
				color: 'hsl(330, 100%, 80%)',
				label: ''
			},
			{
				color: 'hsl(330, 100%, 90%)',
				label: ''
			},
		],
		columns: 9,
	}
};

export default Editor;
